import MarkdownIt from 'markdown-it'
import type Token from 'markdown-it/lib/token.mjs'

/**
 * Factory pinata per istanze markdown-it. Usata sia dall'export desktop
 * (per pre-calcolare blockId nei sidecar blocks.json) sia dal Reader
 * (per render con id sui blocchi top-level). Stesso codice = stesso
 * tokenization = blockId allineati per construction.
 *
 * Vincolo di determinismo: questo modulo è la fonte di verità monorepo-wide
 * per la tokenizzazione. Non modificare le opzioni senza aggiornare tutti
 * i sidecar blocks.json già generati.
 *
 * NOTA: NON include il plugin KaTeX. Il rendering matematico è aggiunto
 * solo in `createMarkdownItForRendering()` che è riservato al WebView renderer.
 * La tokenizzazione di base deve rimanere invariata per garantire il determinismo
 * dei blockId nei sidecar.
 */
export function createMarkdownIt(): MarkdownIt {
  const md = new MarkdownIt({
    html: false,
    linkify: true,
    typographer: false, // disabilitato per determinismo: niente smartquote
    breaks: false,
  })
  return md
}

/**
 * Hook per il renderer HTML del WebView Reader: applica i plugin di rendering
 * (es. KaTeX per LaTeX) a un'istanza MarkdownIt costruita altrove.
 *
 * I plugin specifici del WebView (KaTeX, ecc.) vivono nel package consumer
 * (`apps/reader`) per evitare di trascinare le loro dipendenze nel bundle
 * browser del desktop, che invece importa solo `createMarkdownIt()` per
 * generare i sidecar deterministici.
 *
 * ATTENZIONE: NON applicare plugin che modificano la tokenizzazione di base
 * a un'istanza usata per `extractTopLevelBlocks` — i blockId sidecar devono
 * restare invariati.
 */
export function applyRenderingPlugins(
  md: MarkdownIt,
  plugins: Array<(md: MarkdownIt) => void>,
): MarkdownIt {
  for (const apply of plugins) apply(md)
  return md
}

/**
 * Estrae i blocchi top-level da un markdown e produce un array di
 * `{ blockId, text }` deterministicamente.
 *
 * Algoritmo:
 * - Tokenizza via `md.parse(markdown, {})`.
 * - Raggruppa i token a nesting level 0 in blocchi top-level.
 * - Per ciascun blocco estrae il textContent plain (heading, paragraph,
 *   list, code block, fence, blockquote, hr, ecc.).
 * - Genera blockId = `${subParaId}/b${counter}` con counter da 1.
 *
 * Vincolo: stesso input → stesso output (deterministico).
 */
export function extractTopLevelBlocks(
  markdown: string,
  subParaId: string,
): { blockId: string; text: string }[] {
  if (!markdown.trim()) return []

  const md = createMarkdownIt()
  const tokens = md.parse(markdown, {})
  const blocks: { blockId: string; text: string }[] = []
  let counter = 0

  // Token di apertura dei blocchi top-level (nesting = +1, level = 0)
  const OPEN_TOKENS = new Set([
    'paragraph_open',
    'heading_open',
    'blockquote_open',
    'bullet_list_open',
    'ordered_list_open',
    'table_open',
    'dl_open',
    'html_block',
  ])
  // Token che sono blocchi self-contained senza open/close
  const SELF_TOKENS = new Set(['code_block', 'fence', 'hr'])

  let i = 0
  while (i < tokens.length) {
    const token = tokens[i]
    if (!token) {
      i++
      continue
    }

    if (token.level === 0 && SELF_TOKENS.has(token.type)) {
      // Blocchi self-contained: code_block, fence, hr
      const text = (token.content ?? '').trimEnd()
      counter++
      blocks.push({ blockId: `${subParaId}/b${counter}`, text })
      i++
      continue
    }

    if (token.level === 0 && OPEN_TOKENS.has(token.type)) {
      // Raccoglie tutto fino al token di chiusura corrispondente
      const openType = token.type
      const closeType = openType.replace('_open', '_close')
      const textParts: string[] = []
      let depth = 1
      i++

      while (i < tokens.length && depth > 0) {
        const inner = tokens[i]
        if (!inner) {
          i++
          continue
        }

        if (inner.type === openType) depth++
        else if (inner.type === closeType) {
          depth--
          i++
          continue
        }

        // Estrae testo da token inline e simili
        textParts.push(...extractTextFromToken(inner, tokens))
        i++
      }

      const text = textParts.join('').trim()
      if (text.length > 0 || openType !== 'hr') {
        counter++
        blocks.push({ blockId: `${subParaId}/b${counter}`, text })
      }
      continue
    }

    i++
  }

  return blocks
}

/**
 * Estrae il testo plain da un singolo token, ricorsivamente per inline.
 */
function extractTextFromToken(token: Token, _allTokens: Token[]): string[] {
  const parts: string[] = []

  if (token.type === 'inline' && token.children) {
    for (const child of token.children) {
      if (child.type === 'text' || child.type === 'code_inline') {
        parts.push(child.content)
      } else if (child.type === 'softbreak' || child.type === 'hardbreak') {
        parts.push(' ')
      }
      // Ignora tag HTML inline, image, ecc.
    }
  } else if (token.type === 'code_block' || token.type === 'fence') {
    parts.push(token.content)
  } else if (token.type === 'text') {
    parts.push(token.content)
  }

  return parts
}

/**
 * Extracts top-level blocks from markdown and returns raw markdown source for each block.
 * Unlike extractTopLevelBlocks (which returns plain text), this preserves all markdown
 * formatting (bold, italic, tables, code, links, etc.).
 *
 * Algorithm: tokenize via md.parse, use token.map to slice the original source lines.
 * Only top-level open tokens (level=0) or self-contained blocks are considered.
 * blockId = `${subParaId}/b${counter}` with counter from 1.
 */
export function extractTopLevelBlocksRaw(
  markdown: string,
  subParaId: string,
): { blockId: string; markdown: string }[] {
  if (!markdown.trim()) return []

  const md = createMarkdownIt()
  const tokens = md.parse(markdown, {})
  const sourceLines = markdown.split('\n')
  const blocks: { blockId: string; markdown: string }[] = []
  let counter = 0

  for (const token of tokens) {
    if (token.level !== 0) continue
    if (!token.map) continue

    const isOpen = token.type.endsWith('_open')
    const isSelf = ['code_block', 'fence', 'hr', 'html_block'].includes(token.type)
    if (!isOpen && !isSelf) continue

    const [startLine, endLine] = token.map
    const blockRaw = sourceLines.slice(startLine, endLine).join('\n').trim()
    if (blockRaw.length === 0) continue

    counter++
    blocks.push({ blockId: `${subParaId}/b${counter}`, markdown: blockRaw })
  }

  return blocks
}

/**
 * Regex that matches the bold prefix an LLM writes at the start of a typed block:
 *   **Integrazione:** / **Integrazione** / **Correzione:** / **Chiarimento:** etc.
 * Used by renderers to strip the redundant inline label when a block-label heading
 * already shows the type. Single source of truth — import from here, do not copy.
 */
export const BLOCK_MARKER_RE = /^\s*\*\*(?:Integrazione|Correzione|Chiarimento):?\*\*:?\s*/i

const INTEGRATION_RE = /^\s*\*\*Integrazione:?\*\*/i
const CORRECTION_RE = /^\s*\*\*Correzione:?\*\*/i
const CLARIFICATION_RE = /^\s*\*\*Chiarimento:?\*\*/i

export function stripBlockPrefix(markdown: string): string {
  return markdown.replace(BLOCK_MARKER_RE, '')
}

/**
 * Normalise a flat block array at render time, fixing two patterns that arise
 * in bundles generated before the pipeline fix landed:
 *
 * 1. Paragraph blocks whose markdown starts with a marker prefix
 *    (**Integrazione:** / **Correzione:** / **Chiarimento:**) are promoted to
 *    the correct kind.  This fixes bundles where parseBlocks ran without
 *    clarification detection.
 *
 * 2. A non-paragraph block whose markdown ends with ':' is merged with the
 *    immediately following paragraph block (typically an ordered/unordered
 *    list that the markdown tokenizer split into a separate top-level token).
 *    This fixes the "list outside the callout card" visual bug for existing
 *    bundles.
 *
 * Idempotent: already-correct blocks are returned unchanged.
 * Call this once per part.blocks before passing the array to the renderer.
 */
import type { BundleManualeBlock } from '@arianna/shared'

export function normalizeBlocks(blocks: readonly BundleManualeBlock[]): BundleManualeBlock[] {
  // Pass 1: promote mistyped paragraph blocks
  const promoted: BundleManualeBlock[] = blocks.map((b) => {
    if (b.kind !== 'paragraph') return b
    if (INTEGRATION_RE.test(b.markdown)) return { ...b, kind: 'integration' }
    if (CORRECTION_RE.test(b.markdown)) return { ...b, kind: 'correction' }
    if (CLARIFICATION_RE.test(b.markdown)) return { ...b, kind: 'clarification' }
    return b
  })

  // Pass 2: absorb continuation blocks into preceding marker that ends with ':'
  const merged: BundleManualeBlock[] = []
  for (const block of promoted) {
    const prev = merged[merged.length - 1]
    if (
      block.kind === 'paragraph' &&
      prev &&
      prev.kind !== 'paragraph' &&
      prev.markdown.trimEnd().endsWith(':')
    ) {
      merged[merged.length - 1] = { ...prev, markdown: prev.markdown + '\n\n' + block.markdown }
    } else {
      merged.push(block)
    }
  }

  return merged
}

export type { default as MarkdownIt } from 'markdown-it'
